"use client";

import { Link } from "@/navigation";
import { ArrowRight } from "lucide-react";
import { useTranslations, useLocale } from "next-intl";

const EVENT_CONFIGS = [
  { key: "wedding", emoji: "💍", href: "/", accent: "#9A5A00", bg: "#FFF6E7", border: "rgba(209, 136, 38, 0.35)", englishOnly: false },
  { key: "konfirmation", emoji: "🎉", href: "/konfirmation", accent: "#7E5645", bg: "#FBF2EE", border: "rgba(170, 121, 96, 0.32)", englishOnly: false },
  { key: "barnedab", emoji: "👶", href: "/barnedab", accent: "#57428A", bg: "#F3EFFF", border: "rgba(106, 81, 146, 0.3)", englishOnly: false },
  { key: "barmitsva", emoji: "✡️", href: "/bar-mitsva", accent: "#1F3D78", bg: "#ECF2FF", border: "rgba(46, 88, 164, 0.28)", englishOnly: true },
  { key: "fodselsdag", emoji: "🎂", href: "/fodselsdag", accent: "#944C09", bg: "#FFF4E7", border: "rgba(201, 122, 28, 0.34)", englishOnly: false },
  { key: "julefrokost", emoji: "🎄", href: "/julefrokost", accent: "#7E2622", bg: "#FDEEEE", border: "rgba(170, 58, 58, 0.28)", englishOnly: false },
] as const;

export default function EventTypeMarquee() {
  const t = useTranslations("EventTypeMarquee");
  const locale = useLocale();
  const events = EVENT_CONFIGS.filter((e) => !e.englishOnly || locale === "en");
  const scrollDuration = Math.max(24, events.length * 5.5);

  const renderCards = (copyIndex: number) =>
    events.map((event) => (
      <Link
        key={`${copyIndex}-${event.href}`}
        href={event.href}
        className="group/item relative flex w-[208px] shrink-0 flex-col justify-between overflow-hidden rounded-2xl border px-4 py-3 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_32px_-20px_rgba(40,26,13,0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 md:w-[232px]"
        style={{ background: event.bg, borderColor: event.border }}
      >
        <div className="flex items-start gap-3">
          <span className="text-2xl leading-none select-none">{event.emoji}</span>
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-tight" style={{ color: event.accent }}>
              {t(`events.${event.key}.name`)}
            </p>
            <p className="mt-1 text-xs leading-snug text-muted-foreground">
              {t(`events.${event.key}.desc`)}
            </p>
          </div>
        </div>
        <div
          className="mt-4 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.12em] opacity-80 transition-opacity group-hover/item:opacity-100"
          style={{ color: event.accent }}
        >
          {t("cta")}
          <ArrowRight className="h-3.5 w-3.5" />
        </div>
      </Link>
    ));

  const renderMobileCards = () =>
    events.map((event) => (
      <Link
        key={`mobile-${event.href}`}
        href={event.href}
        className="group/item relative flex min-h-[68px] items-center gap-2.5 overflow-hidden rounded-xl border px-2.5 py-2 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_32px_-20px_rgba(40,26,13,0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
        style={{ background: event.bg, borderColor: event.border }}
      >
        <span className="text-lg leading-none select-none">{event.emoji}</span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold leading-tight" style={{ color: event.accent }}>
            {t(`events.${event.key}.name`)}
          </p>
          <p className="truncate text-[11px] leading-snug text-muted-foreground">
            {t(`events.${event.key}.desc`)}
          </p>
        </div>
        <div
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border opacity-80 transition-opacity group-hover/item:opacity-100"
          style={{ color: event.accent, borderColor: event.border }}
        >
          <ArrowRight className="h-3.5 w-3.5" />
        </div>
      </Link>
    ));

  return (
    <section className="w-full px-4 md:px-6" aria-label={t("ariaLabel")}>
      <div
        className="mx-auto w-full max-w-screen-xl rounded-2xl border border-[#f3e5c6] px-3 py-5 sm:px-5 md:rounded-[2rem] md:px-6 md:py-10 lg:px-8"
        style={{
          background: "radial-gradient(120% 180% at 50% -20%, #fff3d8 0%, #fff8eb 42%, #ffffff 100%)",
        }}
      >
        <div className="mb-3 flex flex-col items-center gap-1.5 text-center md:mb-7 md:gap-3">
          <p className="inline-flex items-center rounded-full border border-[#efdcb8] bg-white/80 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b6b32] md:px-4 md:text-[11px] md:tracking-[0.18em]">
            {t("label")}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-2 md:hidden">{renderMobileCards()}</div>

        <div className="group relative hidden overflow-hidden rounded-2xl border border-white/80 bg-white/75 py-4 shadow-[0_20px_45px_-40px_rgba(91,66,24,0.6)] backdrop-blur-sm md:block">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-[#fff9ee] to-transparent sm:w-28"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-[#fff9ee] to-transparent sm:w-28 md:max-lg:w-40 md:max-lg:via-[#fff9ee]/70"
          />
          <div
            className="group-hover:[animation-play-state:paused] group-focus-within:[animation-play-state:paused] motion-reduce:[animation:none]"
            style={{
              display: "flex",
              width: "max-content",
              animation: `event-marquee ${scrollDuration}s linear infinite`,
              willChange: "transform",
            }}
          >
            <div className="flex shrink-0 items-stretch gap-3 pr-3 md:gap-4 md:pr-4">
              {renderCards(0)}
            </div>
            <div className="flex shrink-0 items-stretch gap-3 pr-3 md:gap-4 md:pr-4">
              {renderCards(1)}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
